import React, { useState } from 'react';
import { Modal, Input, Select, Button } from '../../../shared/components';
import PatientSearch from './PatientSearch';
import VitalsForm from './VitalsForm';
import { useVisitRegistration } from '../hooks/useVisitRegistration';
//import './VisitRegistrationModal.css';

const VisitRegistrationModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Patient Selection, 2: Visit Details, 3: Vitals
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [visitDetails, setVisitDetails] = useState({
    visit_type_id: '',
    chief_complaint: '',
    user_id: '', // doctor id
    visit_date: '',
    is_emergency: false,
  });
  const [vitals, setVitals] = useState({
    temperature: '',
    heart_rate: '',
    blood_pressure_systolic: '',
    blood_pressure_diastolic: '',
    respiratory_rate: '',
    oxygen_saturation: '',
    weight: '',
    height: '',
    notes: '',
  });

  const { loading, error, visitTypes, doctors, searchPatients, registerVisit, completeVisit } = useVisitRegistration();

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setStep(2);
  };

  const handleVisitDetailsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setVisitDetails(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleVitalsChange = (field, value) => {
    setVitals(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleVisitDetailsSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPatient) return;

    setStep(3);
  };

  const handleVitalsSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // First register the visit
      const visitData = {
        patient_id: selectedPatient.id,
        ...visitDetails,
        visit_status: 'completed', // Mark as completed since we're taking vitals now
        organization_id: selectedPatient.organization_id,
        ohc_id: selectedPatient.ohc_id,
      };
      
      const registeredVisit = await registerVisit(visitData);
      
      // Then add vitals and complete the visit
      if (registeredVisit && registeredVisit.id) {
        await completeVisit(registeredVisit.id, vitals);
      }
      
      onSuccess?.();
      resetForm();
      onClose();
    } catch (err) {
      console.error('Failed to complete visit registration:', err);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSelectedPatient(null);
    setVisitDetails({
      visit_type_id: '',
      chief_complaint: '',
      user_id: '',
      visit_date: '',
      is_emergency: false,
    });
    setVitals({
      temperature: '',
      heart_rate: '',
      blood_pressure_systolic: '',
      blood_pressure_diastolic: '',
      respiratory_rate: '',
      oxygen_saturation: '',
      weight: '',
      height: '',
      notes: '',
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Format visit types for select dropdown
  const visitTypeOptions = visitTypes.map(type => ({
    value: type.id,
    label: `${type.name} (${type.duration} min)`
  }));

  // Format doctors for select dropdown
  const doctorOptions = doctors.map(doctor => ({
    value: doctor.id,
    label: `Dr. ${doctor.first_name} ${doctor.last_name}`
  }));

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="registration-step">
            <h3>Select Patient</h3>
            <PatientSearch onPatientSelect={handlePatientSelect} />
          </div>
        );
      
      case 2:
        return (
          <div className="registration-step">
            <h3>Visit Details</h3>
            <div className="selected-patient-info">
              <strong>Patient:</strong> {selectedPatient?.full_name}
              <br />
              <small>ID: {selectedPatient?.patient_code} | DOB: {selectedPatient?.date_of_birth}</small>
            </div>
            
            <form onSubmit={handleVisitDetailsSubmit}>
              <Select
                label="Visit Type"
                name="visit_type_id"
                value={visitDetails.visit_type_id}
                onChange={handleVisitDetailsChange}
                options={visitTypeOptions}
                required
              />
              
              <Input
                label="Chief Complaint"
                name="chief_complaint"
                value={visitDetails.chief_complaint}
                onChange={handleVisitDetailsChange}
                placeholder="Describe the reason for visit"
                required
              />
              
              <Select
                label="Doctor"
                name="user_id"
                value={visitDetails.user_id}
                onChange={handleVisitDetailsChange}
                options={doctorOptions}
                required
              />
              
              <Input
                label="Visit Date & Time"
                name="visit_date"
                type="datetime-local"
                value={visitDetails.visit_date}
                onChange={handleVisitDetailsChange}
                required
              />
              
              <div className="checkbox-field">
                <label>
                  <input
                    type="checkbox"
                    name="is_emergency"
                    checked={visitDetails.is_emergency}
                    onChange={handleVisitDetailsChange}
                  />
                  Emergency Visit
                </label>
              </div>
              
              <div className="form-actions">
                <Button type="button" variant="secondary" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button type="submit" loading={loading}>
                  Next: Record Vitals
                </Button>
              </div>
            </form>
          </div>
        );
      
      case 3:
        return (
          <div className="registration-step">
            <h3>Record Vitals</h3>
            <div className="selected-patient-info">
              <strong>Patient:</strong> {selectedPatient?.full_name}
              <br />
              <strong>Visit Type:</strong> {visitTypes.find(t => t.id === parseInt(visitDetails.visit_type_id))?.name}
            </div>
            
            <form onSubmit={handleVitalsSubmit}>
              <VitalsForm vitals={vitals} onChange={handleVitalsChange} />
              
              {error && <div className="error-message">{error}</div>}
              
              <div className="form-actions">
                <Button type="button" variant="secondary" onClick={() => setStep(2)}>
                  Back
                </Button>
                <Button type="submit" loading={loading}>
                  Complete Registration
                </Button>
              </div>
            </form>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Register New Visit"
      size="lg"
    >
      <div className="visit-registration-modal">
        <div className="step-indicator">
          <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            1. Select Patient
          </div>
          <div className={`step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
            2. Visit Details
          </div>
          <div className={`step ${step >= 3 ? 'active' : ''}`}>
            3. Record Vitals
          </div>
        </div>
        
        {renderStep()}
      </div>
    </Modal>
  );
};

export default VisitRegistrationModal;